import { useCallback, useMemo } from "react";

import { useToast } from "shared/hooks";

import { useGamesApiService } from "shared/services";

import { useGamesStore } from "../contexts";

import type { GetListGamesRequestParamsModel, LaunchGameRequestParamsModel } from "../types/GameModel";

const useGames = () => {
	const gamesService = useGamesApiService();

	const store = useGamesStore();
	const { setState, setInitial } = useGamesStore();

	const { showToast } = useToast();

	const methods = useMemo(() => {
		return {
			async getGames(params: GetListGamesRequestParamsModel) {
				try {
					setState({ loading: true });
					const res = await gamesService.getGames(params);
					if (res?.data?.games) {
						setState({
							categories: res.data.categories,
							games: res.data.games,
							merchants: Object.values(res.data.merchants)
						});
					}
					return {
						categories: res.data.categories,
						games: res.data.games,
						merchants: []
					};
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't get list of games", noIcon: true });
					return {
						categories: [],
						games: [],
						merchants: []
					};
				} finally {
					setState({ loading: false });
				}
			},
			async launchGame(params: LaunchGameRequestParamsModel) {
				try {
					setState({ loading: true });
					const res = await gamesService.launchGame(params);
					if (res?.data?.gameHtml) {
						return res.data;
					}
					return null;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't launch game", noIcon: true });
					return null;
				} finally {
					setState({ loading: false });
				}
			},
			async listFavorites() {
				try {
					setState({ loadingFavoriteGames: true });
					const res = await gamesService.listFavorites();
					setState({ favoriteGames: res.data || [] });
					return res?.data || [];
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't get list of favorite games", noIcon: true });
					return [];
				} finally {
					setState({ loadingFavoriteGames: false });
				}
			},
			async favorite(gameId: string) {
				try {
					setState({ makingFavorite: true });
					const res = await gamesService.favorite(gameId);
					setState(ctx => {
						const favorites = [...ctx.favoriteGames];
						if (res.data.favorite) {
							favorites.push({ game_id: gameId });
						} else {
							const index = favorites.findIndex(x => x.game_id === gameId);
							if (index > -1) {
								favorites.splice(index, 1);
							}
						}
						return { favoriteGames: favorites };
					});
					return res?.data?.favorite || false;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't mark game as favorite", noIcon: true });
					return false;
				} finally {
					setState({ makingFavorite: false });
				}
			},
			resetStore() {
				setInitial();
			}
		};
	}, [setState, gamesService, showToast, setInitial]);

	const getData = useCallback(() => {
		return store;
	}, [store]);

	return { ...methods, getData };
};

export default useGames;
